import { RefObject, useCallback, useEffect, useState } from 'react';
import { Editor, Node as SlateNode, Path, Range } from 'slate';
import { ReactEditor } from 'slate-react';

import { isValidRange } from '../internal/utils';
import { aboveWithSelectionMenu, ElementWithSelectionMenu } from './aboveWithSelectionMenu';

type Args = {
  editor: ReactEditor;
  selectionPreservingContainer: RefObject<HTMLElement>;
};

export type MenuAnchor = {
  elementWithSelectionMenu: {
    node: ElementWithSelectionMenu;
    path: Path;
  } | null;
  anchorEl: HTMLElement;
  selection: Range;
} | null;

export function useMenuAnchor({ editor, selectionPreservingContainer }: Args): MenuAnchor {
  const [menuAnchorState, setMenuAnchorState] = useState<MenuAnchor>(null);
  const { selection } = editor;

  const resetMenuAnchorState = useCallback(() => {
    setMenuAnchorState(null);
  }, []);
  useListenToClicksOutsideContainer({ containerRef: selectionPreservingContainer, onClick: resetMenuAnchorState });

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;

    /**
     * Synchronize editor selection with DOM state, retrieving the DOM element which should be the anchor
     *
     * Note that this method is scheduled to run with a timeout if an error is thrown.
     */
    const syncDomState = (): void => {
      if (selection != null) {
        const elementWithSelectionMenu = aboveWithSelectionMenu({ editor, selection });

        const node =
          elementWithSelectionMenu != null
            ? ((elementWithSelectionMenu.node as unknown) as SlateNode)
            : Editor.node(editor, selection)[0];

        try {
          const anchorEl = ReactEditor.toDOMNode(editor, node);

          setMenuAnchorState({
            anchorEl,
            selection,
            elementWithSelectionMenu,
          });
        } catch (e) {
          // Editor state can differ from what has been flushed to the DOM despite executing this in `useEffect`
          // As such, `ReactEditor.toDOMNode` can throw.
          setMenuAnchorState(null);
          timeoutId = setTimeout(syncDomState, 100);
        }
      }
    };

    syncDomState();

    return (): void => {
      if (timeoutId != null) {
        clearTimeout(timeoutId);
      }
    };
  }, [editor, selection]);

  if (menuAnchorState != null && (selection != null || isValidRange(editor, menuAnchorState.selection))) {
    return { ...menuAnchorState, selection: selection || menuAnchorState.selection };
  } else {
    return null;
  }
}

function useListenToClicksOutsideContainer({
  containerRef,
  onClick,
}: {
  containerRef: RefObject<HTMLElement>;
  onClick: () => void;
}): void {
  useEffect(() => {
    const onMouseDown = (e: MouseEvent): void => {
      if (e.target != null && containerRef.current != null && !containerRef.current.contains(e.target as Node)) {
        onClick();
      }
    };

    document.addEventListener('mousedown', onMouseDown);

    return (): void => {
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [containerRef, onClick]);
}
